export default (route, params, options) => ({
    route,
    params,
    selectedTiles: [],
    storageData: {},
    config: {
        containerWidth: 0,
        containerHeight: 0,
        segmentRowHeight: 200,
        segmentsMargin: 8,
        sectionMargin: 8,
        removeOldSectionAfter: 3,
        labelHeight: 32,
        previewMaxWidth: 0,
    },
    tileSize: {
        width: 0,
        height: 200,
    },
    allSelected: false,
    totalTiles: 0,
    tilesState: {},
    lastSelectedTileIndex: null,
    sections: [],
    sectionSize: 0,
    segmentSize: 0,
    gridScrollNode: null,
    options: {
        ...{
            preview: true,
            showHighResLink: true,
            tags: false,
            sort: 'name',
            gridContainerId: 'gridContainerPhotoView',
            selectable: true,
            showHighRes: true,
        },
        ...options,
    },
    debounceResize: null,
    debounceRender: null,
    scrollDirection: null,
    container: null,
    tileTemplate: null,
    fetchPriorityObserver: null,
    selectionIdRanges: [],
    rangesCount: 0,
    shiftWasPressed: false,
    unselectedAfterShiftSelect: [],
    totalTilesCount: 0,
    unselectedTiles: [],
    fetchingImages: false,
    renderToken: 0,
    resizeObserver: null,
    renderRetry: null,
    rendering: false,
    emptyStateMounted: false,

    init() {
        this.reset();
        this.start();

        this.initWatches();
    },

    initWatches() {
        this.$watch('tilesState', (tiles) => {
            if (this.allSelected) {
                this.selectedTiles = [];

                return;
            }

            this.allSelected = this.totalTilesCount === this.totalTiles && this.totalTiles !== 0;

            Object.values(tiles).forEach((tile) => {
                if (!tile?.id || tile.selected) return;

                const isOnRange = this.selectionIdRanges.some((currentRange) => {
                    return currentRange.start_id <= tile.id && currentRange.end_id >= tile.id;
                });

                if (isOnRange && !this.unselectedAfterShiftSelect.includes(tile.id)) {
                    if (!tile.selected) {
                        tile.selected = true;
                    }
                }
            });
        });
    },

    start() {
        this.cancelPendingRender();

        this.$nextTick(() => {
            this.container = this.findInScope(this.options.gridContainerId);

            if (!this.container) {
                return;
            }

            this.fillTileTemplate();
            this.fillFetchPriorityObserver();
            this.renderGrid();
        });
    },

    cancelPendingRender() {
        this.renderToken++;
        this.rendering = false;
        this.emptyStateMounted = false;

        clearTimeout(this.debounceRender);
        this.debounceRender = null;

        this.stopRenderRetry();
        this.disconnectResizeObserver();

        this.fetchPriorityObserver?.disconnect();
    },

    redrawGrid() {
        this.reset();
        this.start();
    },

    refreshGrid() {
        if (this.isGridRendered()) {
            return;
        }

        this.redrawGrid();
        this.ensureGridRendered();
    },

    ensureGridRendered(interval = 800, timeout = 12000) {
        this.stopRenderRetry();

        const deadline = Date.now() + timeout;

        const tick = (delay) => {
            if (!this.$el?.isConnected || this.isGridRendered() || Date.now() >= deadline) {
                this.stopRenderRetry();

                return;
            }

            const next = Math.min(delay * 2, timeout);

            if (!this.rendering) {
                this.redrawGrid();
            }

            this.renderRetry = setTimeout(() => tick(next), next);
        };

        this.renderRetry = setTimeout(() => tick(interval), interval);
    },

    stopRenderRetry() {
        clearTimeout(this.renderRetry);
        this.renderRetry = null;
    },

    isGridRendered() {
        if (this.emptyStateMounted) {
            return true;
        }

        if (!this.container?.isConnected) {
            return false;
        }

        return Boolean(this.container.querySelector('[data-tile-id], div.section'));
    },

    findInScope(id) {
        return this.$el?.querySelector(`#${id}`) ?? document.getElementById(id);
    },

    renderGrid(delay = 400) {
        clearTimeout(this.debounceRender);

        const token = this.renderToken;

        this.rendering = true;

        this.debounceRender = setTimeout(async () => {
            try {
                if (!this.hasContainerSize()) {
                    this.observeContainerSize(token);

                    return;
                }

                const isSized = await this.waitForContainerSize();

                if (token !== this.renderToken) {
                    return;
                }

                if (!isSized) {
                    this.observeContainerSize(token);

                    return;
                }

                this.createGridScroll();
                this.fillSizes();
                this.createDynamicBackgroundClass(this.tileSize.width, this.tileSize.height);

                await this.loadInitialScreen();

                if (token !== this.renderToken) {
                    return;
                }

                this.initHandleScroll();
            } catch (error) {
                console.error('There was a problem rendering the photo grid:', error);
            } finally {
                if (token === this.renderToken) {
                    this.rendering = false;
                }
            }
        }, delay);
    },

    hasContainerSize() {
        if (!this.container) {
            return false;
        }

        const { width, height } = this.container.getBoundingClientRect();

        return width > 0 && height > 0;
    },

    observeContainerSize(token) {
        if (typeof ResizeObserver === 'undefined' || !this.container) {
            return;
        }

        this.disconnectResizeObserver();

        this.resizeObserver = new ResizeObserver(() => {
            if (token !== this.renderToken || !this.hasContainerSize()) {
                return;
            }

            this.disconnectResizeObserver();
            this.renderGrid(0);
        });

        this.resizeObserver.observe(this.container);
    },

    disconnectResizeObserver() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
    },

    waitForContainerSize(timeout = 3000) {
        return new Promise((resolve) => {
            let settled = false;
            let lastWidth = null;

            const finish = (isSized) => {
                if (settled) {
                    return;
                }

                settled = true;
                clearTimeout(timer);
                resolve(isSized);
            };

            const timer = setTimeout(() => finish(this.hasContainerSize()), timeout);

            const check = () => {
                if (settled) {
                    return;
                }

                if (!this.container) {
                    finish(false);

                    return;
                }

                const { width, height } = this.container.getBoundingClientRect();

                if (width > 0 && height > 0 && width === lastWidth) {
                    finish(true);

                    return;
                }

                lastWidth = width;

                requestAnimationFrame(check);
            };

            requestAnimationFrame(check);
        });
    },

    fillTileTemplate() {
        this.tileTemplate = this.findInScope('tile-template').cloneNode(true);
        this.tileTemplate.classList.remove('hidden');
        this.tileTemplate.removeAttribute('id');
    },

    fillFetchPriorityObserver() {
        this.fetchPriorityObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach(({ target: img, isIntersecting }) => {
                    const currentPriority = img.getAttribute('fetchpriority');
                    const newPriority = isIntersecting ? 'high' : 'low';

                    if (currentPriority !== newPriority) {
                        img.setAttribute('fetchpriority', newPriority);
                    }
                });
            },
            {
                rootMargin: '10px',
            },
        );
    },

    fillSizes() {
        this.config.containerWidth = this.container.getBoundingClientRect().width - 18;
        this.config.containerHeight = this.container.getBoundingClientRect().height;

        const breakpoints = [
            { size: 7, min: 1535, max: Infinity },
            { size: 6, min: 1279, max: 1534 },
            { size: 5, min: 1023, max: 1278 },
            { size: 4, min: 767, max: 1022 },
            { size: 3, min: 421, max: 766 },
            { size: 2, min: 0, max: 420 },
        ];

        this.segmentSize =
            breakpoints.find(
                (breakpoint) =>
                    this.config.containerWidth >= breakpoint.min &&
                    this.config.containerWidth <= breakpoint.max,
            )?.size ?? breakpoints.at(-1).size;

        this.sectionSize =
            Math.ceil(this.config.containerHeight / this.config.segmentRowHeight) *
            this.segmentSize;

        this.tileSize.width =
            this.config.containerWidth / this.segmentSize - this.config.segmentsMargin;

        const fourthPercentOfScreen = window.innerWidth * 0.4;
        const minWidthAllowed = 450;

        this.config.previewMaxWidth = Math.min(fourthPercentOfScreen, minWidthAllowed);
    },

    createGridScroll() {
        this.gridScrollNode = document.createElement('div');
        this.gridScrollNode.id = 'photo-view-grid-scroll' + this.container.id;

        this.container.appendChild(this.gridScrollNode);
    },

    async loadInitialScreen() {
        const { groups, total } = await this.getSectionsGrouped();

        this.totalTiles = total;

        if (!total) {
            this.mountEmptyState();
            return;
        }

        await this.createGroups(groups);

        this.setGridScrollHeight();
    },

    setGridScrollHeight() {
        const total = this.sections.reduce((total, section) => total + section.height, 0);

        this.gridScrollNode.style.height = `${total}px`;
    },

    async createGroups(groups) {
        const firstSection = groups[0].sections.shift();
        const firstLabel = groups[0].label;

        await this.loadFirstSection(firstSection, firstLabel);

        let lastHeight = 0;

        groups.forEach((group) => {
            const { label, sections } = group;
            let labelTop = 0;
            if (!sections.length) {
                return;
            }

            if (label && groups.indexOf(group) !== 0) {
                const lastSection = Object.values(this.sections).at(-1);
                lastHeight = lastSection.top + lastSection.height;
                labelTop = this.config.sectionMargin * 2;

                this.mountGroupLabel(label, labelTop + lastHeight);

                labelTop = labelTop + this.config.labelHeight / 2;
            }

            this.mountSections(sections, labelTop);
        });

        this.loadMoreSections();
    },

    mountGroupLabel(label, top) {
        if (!label) {
            return;
        }

        const labelHtml = `<div class="group-label contain-layout absolute flex items-center text-md z-10" style="top: ${top}px; height: ${this.config.labelHeight}px; left: 8px">${label}</div>`;

        this.gridScrollNode.insertAdjacentHTML('beforeend', labelHtml);
    },

    async loadFirstSection(section, groupLabel = '') {
        this.mountGroupLabel(groupLabel, 0);

        const startFrom = groupLabel ? this.config.labelHeight : 0;

        await this.mountSections([section], startFrom);

        const [id] = Array.isArray(section) ? section : [section];
        await this.setNextSection(this.sections[id]);
        this.loadSectionData();
    },

    loadSectionData() {
        const sectionsToUpdate = this.sections.filter((section) => !section.shouldMountEmptyState);

        sectionsToUpdate.forEach((section) => {
            if (!this.sections[section.id]?.shouldUpdate) {
                return;
            }

            const sectionDiv = document.getElementById(
                `${this.container.id}-photo-view-section-${section.id}`,
            );

            this.handleSingleSection(sectionDiv);
        });
    },

    loadMoreSections() {
        const sectionsToLoad = this.sections
            .filter((section) => section.shouldMountEmptyState)
            .slice(0, 2);

        sectionsToLoad.forEach(async (section) => {
            await this.setNextSection(section);
        });

        setTimeout(() => this.loadSectionData(), 200);
    },

    handleSingleSection(sectionDiv) {
        const sectionId = this.splitId(sectionDiv.id);

        if (!this.sections[sectionId]?.shouldUpdate) {
            return;
        }

        this.sections[sectionId].shouldUpdate = false;

        this.getSegments(sectionDiv.id).then(async (segments) => {
            sectionDiv.classList.remove('photo-view-loading-grid');
            this.populateSection(sectionDiv, segments);
        });
    },

    tileStateObject(image, sectionId = null, segmentId = null) {
        const selected = this.selectedTiles.includes(image.id) || this.allSelected;

        return {
            id: image.id,
            event_id: image.event_id ?? null,
            deleted: image.deleted_at ?? null,
            teams: image.teams ?? [],
            tags: image.teams?.map((team) => team.id) ?? [],
            selected,
            segmentId,
            sectionId,
        };
    },

    fillTilesState(segments, sectionId = null) {
        return new Promise((resolve) => {
            segments.forEach((segment) => {
                segment.images.forEach((image, imageIndex) => {
                    this.tilesState[`${segment.segmentId}${imageIndex + 1}`] = this.tileStateObject(
                        image,
                        sectionId,
                        segment.segmentId,
                    );
                });
            });

            resolve();
        });
    },

    populateSection(sectionDiv, segments) {
        const fragment = document.createDocumentFragment();

        const segmentDivTemplate = document.createElement('div');
        segmentDivTemplate.className = 'segment grid';
        segmentDivTemplate.style.cssText = `
        width: ${this.config.containerWidth}px;
        height: ${this.config.segmentRowHeight}px;
                grid-template-columns: repeat(${this.segmentSize}, 1fr);
            `;
        for (const segment of segments) {
            const segmentDiv = segmentDivTemplate.cloneNode(true);
            segmentDiv.id = `photo-view-segment-${segment.segmentId}`;

            segmentDiv.append(...this.generateTiles(segment));

            fragment.appendChild(segmentDiv);
        }

        requestAnimationFrame(() => {
            sectionDiv.appendChild(fragment);
            this.addSelectClick(sectionDiv);
            this.addFetchPriorityObserver(sectionDiv);
        });
    },

    *generateTiles({ images: tiles, segmentId }) {
        for (const [index, tile] of tiles.entries()) {
            yield this.getTileElement(tile, `${segmentId}${index + 1}`);
        }
    },

    cancelPendingImageRequests(sectionDiv) {
        const images = sectionDiv.querySelectorAll('img[data-image-id]');

        images.forEach((img) => {
            img.removeAttribute('src');
        });
    },

    initHandleScroll() {
        this.scrollDirection = 'down';
        let lastScrollY = this.container.scrollY;
        let lastTime = Date.now();
        let scrollTimeout;

        const calculateDelay = (speed) => {
            return Math.max(20, Math.min(100, speed * 100));
        };

        const handleScrollStop = () => {
            this.loadSectionData();
        };

        const removeOldSections = () => {
            let sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');

            while (sectionsDivs.length > this.config.removeOldSectionAfter) {
                const indexToRemove = this.scrollDirection === 'down' ? 0 : sectionsDivs.length - 1;

                const sectionToRemove = sectionsDivs[indexToRemove];
                const sectionId = this.splitId(sectionToRemove.id);

                this.cancelPendingImageRequests(sectionToRemove);

                this.sections[sectionId].shouldMountEmptyState = true;
                this.sections[sectionId].shouldUpdate = true;

                this.gridScrollNode.removeChild(sectionToRemove);
                sectionsDivs = this.gridScrollNode.querySelectorAll('div.section');
            }
        };

        const reorderGrid = () => {
            const gridItems = Array.from(this.gridScrollNode.children);

            gridItems.sort((a, b) => {
                const idA = parseInt(a.id.replace(/\D/g, ''), 10);
                const idB = parseInt(b.id.replace(/\D/g, ''), 10);

                return idA - idB;
            });

            gridItems.forEach((item) => this.gridScrollNode.appendChild(item));
        };

        this.container.addEventListener(
            'scroll',
            async () => {
                const currentY = this.container.scrollTop;
                const distance = Math.abs(currentY - lastScrollY);
                this.scrollDirection = currentY > lastScrollY ? 'down' : 'up';

                const currentTime = Date.now();
                const timeDiff = currentTime - lastTime || 1;
                const speed = distance / timeDiff;

                lastScrollY = currentY;
                lastTime = currentTime;

                const delay = calculateDelay(speed);

                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(handleScrollStop, delay);

                await this.loadSectionEmpty();
                removeOldSections();
            },
            { passive: true },
        );
    },

    async loadSectionEmpty() {
        const getIntersectingSections = () => {
            const container = this.container;
            const currentTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            const currentBottom = currentTop + containerHeight;
            const sections = [];

            let iterations = 0;
            let startCountInteractions = false;

            this.sections.some((section, index) => {
                const { top: sectionTop, height } = section;
                const sectionBottom = sectionTop + height;

                const isVisible =
                    (sectionTop >= currentTop && sectionTop <= currentBottom) ||
                    (sectionBottom >= currentTop && sectionBottom <= currentBottom) ||
                    (sectionTop <= currentTop && sectionBottom >= currentBottom);

                if (isVisible) {
                    startCountInteractions = true;
                    sections.push(section);
                }

                if (sections.length >= 2) {
                    return true;
                }

                if (startCountInteractions) {
                    iterations++;
                }

                return iterations >= 2 && sections.length === 1;
            });

            getNextSection(sections);

            return sections;
        };

        const getNextSection = (sections) => {
            if (sections.length <= 1) {
                return;
            }

            const lastSection = sections.at(-1);
            const firstSection = sections.at(0);

            const isSequenceIncreasing = this.scrollDirection === 'down';
            const nextIndex = isSequenceIncreasing ? lastSection.id + 1 : firstSection.id - 1;
            const nextSection = this.sections[nextIndex];

            if (nextSection && nextSection.shouldMountEmptyState) {
                sections.push(nextSection);
            }
        };

        const sections = getIntersectingSections();

        for (const section of sections) {
            const sectionState = this.sections[section.id];

            if (sectionState.shouldMountEmptyState) {
                sectionState.shouldMountEmptyState = false;
                await this.setNextSection(section);
            }
        }
    },

    async setNextSection(section) {
        return new Promise((resolve) => {
            this.sections[section.id].shouldMountEmptyState = false;

            requestAnimationFrame(() => {
                const whereAdd = this.scrollDirection === 'down' ? 'beforeend' : 'afterbegin';

                this.gridScrollNode.insertAdjacentHTML(whereAdd, section.html);
                resolve();
            });
        });
    },

    initHandleResize() {
        window.addEventListener('resize', () => {
            clearTimeout(this.debounceResize);

            this.debounceResize = setTimeout(async () => {
                this.reset();
                this.renderGrid();
            }, 500);
        });
    },

    reset() {
        this.selectedTiles = [];
        this.storageData = null;
        this.storageData = {};
        this.allSelected = false;
        this.totalTiles = 0;
        this.tilesState = {};
        this.lastSelectedTileIndex = null;
        this.sections = null;
        this.sections = [];
        this.sectionSize = 0;
        this.segmentSize = 0;
        this.gridScrollNode = null;
        this.debounceResize = null;
        this.debounceRender = null;
        this.scrollDirection = null;

        const grid = this.findInScope(this.options.gridContainerId);

        this.container = null;

        if (grid) {
            grid.innerHTML = '';
        }
    },

    mountSections(sections, addTop = 0) {
        return new Promise((resolve) => {
            const lastSection = Object.values(this.sections).at(-1);

            let prevSectionEnd = this.config.sectionMargin + addTop;

            if (lastSection) {
                prevSectionEnd += lastSection.top + lastSection.height + this.config.sectionMargin;
            }

            let previousSectionSize = lastSection?.sectionSize
                ? lastSection?.sectionSize + lastSection?.previousSectionSize
                : 0;

            for (const section of sections) {
                const [id, totalImages] = Array.isArray(section)
                    ? section
                    : [section, this.sectionSize];

                const sectionData = {
                    id,
                    shouldUpdate: true,
                    shouldMountEmptyState: true,
                    height: this.estimateSectionHeight(totalImages),
                    top: prevSectionEnd,
                    sectionSize: totalImages,
                    previousSectionSize: previousSectionSize,
                };

                this.sections[id] = {
                    ...sectionData,
                    html: this.getEmptySection(sectionData),
                };

                prevSectionEnd += this.sections[id].height + this.config.sectionMargin;
                previousSectionSize += totalImages;

                const segmentAmount = this.sectionSize / this.segmentSize;

                this.fillTilesEmptyState(segmentAmount, id);
            }

            resolve();
        });
    },

    fillTilesEmptyState(segmentAmount, id) {
        for (let segmentIndex = 1; segmentIndex <= segmentAmount; segmentIndex++) {
            for (let tileIndex = 1; tileIndex <= this.segmentSize; tileIndex++) {
                this.tilesState[`${id}${segmentIndex}${tileIndex}`] = {};
            }
        }
    },

    mountEmptyState() {
        this.emptyStateMounted = true;

        this.container.innerHTML = `
            <div class="w-full h-full flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                <svg class="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                <span class="mt-2 block text-sm font-medium text-gray-900">
                    There're no images found.
                </span>
            </div>`;
    },

    estimateSectionHeight(totalImages) {
        const unwrappedWidth = totalImages * this.tileSize.width;
        const rows = Math.ceil(unwrappedWidth / this.config.containerWidth);
        return (
            rows * (this.config.segmentRowHeight + this.config.sectionMargin) -
            this.config.sectionMargin
        );
    },

    getEmptySection(sectionState) {
        return `<div id="${this.container.id}-photo-view-section-${sectionState.id}" class="contain-layout section photo-view-loading-grid absolute flex flex-col gap-2" style="width: ${this.config.containerWidth}px; height: ${sectionState.height}px; top: ${sectionState.top}px; left: 8px"></div>`;
    },

    getSectionsGrouped() {
        try {
            const params = {
                ...this.params,
                section_size: Number(this.sectionSize) || 6,
            };

            return fetch(`/photo-view/${this.route}/sections?` + new URLSearchParams(params)).then(
                (res) => {
                    return res.json();
                },
            );
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    },

    getImageIdsFromSegmentsAndSections(startSectionId, endSectionId, startIndex, endIndex) {
        const { offset, limit } = this.calculateSelectionParams(
            startSectionId,
            endSectionId,
            startIndex,
            endIndex,
        );

        const params = {
            ...this.params,
            offset,
            limit,
            sort: this.options.sort,
        };

        try {
            return fetch(
                `/photo-view/quality-control/image-ids?` + new URLSearchParams(params),
            ).then((res) => {
                return res.json();
            });
        } catch (error) {
            console.error('There was a problem while fetching image IDs:', error);
        }
    },

    calculateSelectionParams(startSectionId, endSectionId, startIndex, endIndex) {
        const startTilePosition = this.separateTileNumber(startSectionId, startIndex);
        const endTilePosition = this.separateTileNumber(endSectionId, endIndex);

        const offsetDif =
            startTilePosition.segment * this.segmentSize -
            (this.segmentSize - startTilePosition.tile);
        const totalLastSection =
            endTilePosition.segment * this.segmentSize - (this.segmentSize - endTilePosition.tile);

        const offset = this.sections[startSectionId].previousSectionSize + offsetDif;
        const endLastSection = this.sections[endSectionId].previousSectionSize;
        const limit = endLastSection + totalLastSection - offset;

        return {
            offset: offset < 0 ? 0 : offset - 1,
            limit,
        };
    },

    getSegments(sectionIndex) {
        const sort = this.options.sort;

        const sectionId = this.splitId(sectionIndex);

        const sectionData = this.storageData[`section-${sectionId}`];

        if (sectionData) {
            return Promise.resolve(JSON.parse(sectionData));
        }

        try {
            const params = {
                ...this.params,
                section_size: Number(this.sections[sectionId].sectionSize) || 6,
                offset: this.sections[sectionId].previousSectionSize,
                segment_size: this.segmentSize ?? 6,
                section: this.splitId(sectionIndex),
                sort: sort,
            };

            return fetch(`/photo-view/${this.route}/segments?` + new URLSearchParams(params))
                .then((res) => res.json())
                .then((data) => {
                    this.storageData[`section-${sectionId}`] = JSON.stringify(data);

                    this.fillTilesState(data, sectionId);

                    return data;
                });
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        }
    },

    splitId(divId, type = 'section') {
        return divId.split(`photo-view-${type}-`)[1];
    },

    createDynamicBackgroundClass(
        boxWidth = 200,
        boxHeight = 200,
        boxColor = '#3C3D3E',
        spacing = 8,
    ) {
        const totalWidth = boxWidth + spacing;
        const totalHeight = boxHeight + spacing;
        const backgroundColor = '#EBEBEB';

        const style = document.getElementById('photo-view-loading-grid-style') ?? document.createElement('style');
        style.id = 'photo-view-loading-grid-style';
        style.innerHTML = `
            .photo-view-loading-grid {
                background-color: ${boxColor};
                background-image:
                    linear-gradient(90deg, ${backgroundColor} 0, transparent 0, transparent ${boxWidth}px, ${backgroundColor} 0px, ${backgroundColor}),
                    linear-gradient(0deg, ${backgroundColor} 0, transparent 0, transparent ${boxHeight}px, ${backgroundColor} 0px, ${backgroundColor});
                background-size: ${totalWidth}px ${totalHeight}px;
                background-position: 0px 0px, 0px -${spacing}px;
        }`;

        document.head.appendChild(style);
    },

    addSelectClick(sectionDiv) {
        sectionDiv.querySelectorAll('div[data-tile-id]').forEach((el) => {
            el.addEventListener('click', (event) =>
                this.selectTile(el.dataset.tileId, event.shiftKey),
            );
        });
    },

    addFetchPriorityObserver(sectionDiv) {
        sectionDiv.querySelectorAll('div[data-tile-id]>img').forEach((el) => {
            this.fetchPriorityObserver.observe(el);
        });
    },

    async selectTile(tileIndex, shiftPressed) {
        if (!this.options.selectable) {
            return;
        }

        if (
            shiftPressed &&
            this.lastSelectedTileIndex !== undefined &&
            this.options.sort === 'name'
        ) {
            const start = Math.min(this.lastSelectedTileIndex, tileIndex);
            const end = Math.max(this.lastSelectedTileIndex, tileIndex);

            const startTile = this.tilesState[start];
            const endTile = this.tilesState[end];

            if (!startTile || !endTile) return;

            const startSegmentId = parseInt(startTile.segmentId);
            const endSegmentId = parseInt(endTile.segmentId);
            const startSectionId = parseInt(startTile.sectionId);
            const endSectionId = parseInt(endTile.sectionId);

            this.selectTilesInRange(start, end);

            this.updateSelectedTiles();

            if (startSegmentId !== endSegmentId) {
                await this.selectTilesAcrossSegments(start, end, startSectionId, endSectionId);
            }

            this.updateTotalTilesCount();

            this.lastSelectedTileIndex = tileIndex;

            this.shiftWasPressed = true;

            return;
        }

        let tile = this.tilesState[`${tileIndex}`];

        if (tile) {
            tile.selected = !tile.selected;

            if (!tile.selected) {
                this.selectedTiles = this.selectedTiles.filter((id) => id != tile.id);

                if (this.allSelected && !this.unselectedTiles.includes(tile.id)) {
                    this.unselectedTiles.push(tile.id);
                }

                if (this.shiftWasPressed && !this.unselectedAfterShiftSelect.includes(tile.id)) {
                    this.unselectedAfterShiftSelect.push(tile.id);
                }

                if (this.totalTilesCount > 0) {
                    this.totalTilesCount--;
                }
            } else {
                if (!this.selectedTiles.includes(tile.id)) {
                    this.selectedTiles.push(tile.id);
                }

                if (this.allSelected) {
                    this.unselectedTiles = this.unselectedTiles.filter((id) => id !== tile.id);
                    this.unselectedAfterShiftSelect = this.unselectedAfterShiftSelect.filter(
                        (id) => id !== tile.id,
                    );
                }

                this.totalTilesCount++;
            }
        }

        this.lastSelectedTileIndex = tileIndex;
    },

    selectTilesInRange(start, end) {
        for (let i = start; i <= end; i++) {
            let tile = this.tilesState[i];

            if (tile) {
                tile.selected = true;
            }
        }
    },

    separateTileNumber(section, tile) {
        const numStr = String(tile);

        const sectionLength = String(section).length;

        const sectionNumber = parseInt(numStr.slice(0, sectionLength));
        const segmentNumber = parseInt(numStr.slice(sectionLength, -1));
        const tileNumber = parseInt(numStr.slice(-1));

        return {
            section: sectionNumber,
            segment: segmentNumber,
            tile: tileNumber,
        };
    },

    async selectTilesAcrossSegments(firstIndex, lastIndex, startSectionId, endSectionId) {
        this.fetchingImages = true;

        const imageIds = await this.getImageIdsFromSegmentsAndSections(
            startSectionId,
            endSectionId,
            firstIndex,
            lastIndex,
        );

        this.selectionIdRanges = this.mergeRanges(this.groupRangeIds(imageIds));

        this.rangesCount = this.countSelected();

        this.updateTotalTilesCount();

        this.allSelected = this.totalTilesCount === this.totalTiles;

        this.fetchingImages = false;
    },

    updateTotalTilesCount() {
        if (this.rangesCount === 0) {
            this.totalTilesCount = this.selectedTiles.length;
        } else {
            this.totalTilesCount = (this.selectedTiles.length || 0) + this.rangesCount;
        }
    },

    mergeRanges(newRanges) {
        const allRanges = [...this.selectionIdRanges, ...newRanges];

        if (allRanges.length === 0) return [];

        allRanges.sort((a, b) => a.start_id - b.start_id);

        const mergedRanges = [];
        let current = allRanges[0];

        for (let i = 1; i < allRanges.length; i++) {
            const next = allRanges[i];

            if (next.start_id <= current.end_id + 1) {
                current.end_id = Math.max(current.end_id, next.end_id);
            } else {
                mergedRanges.push(current);
                current = next;
            }
        }

        mergedRanges.push(current);

        return mergedRanges;
    },

    groupRangeIds(imageIds) {
        let sorted = imageIds.sort((a, b) => a - b);

        const ranges = [];
        let start = sorted[0];
        let end = sorted[0];

        for (let i = 1; i < sorted.length; i++) {
            if (sorted[i] === end + 1) {
                end = sorted[i];
            } else {
                ranges.push({ start_id: start, end_id: end });
                start = sorted[i];
                end = sorted[i];
            }
        }

        ranges.push({ start_id: start, end_id: end });

        return ranges;
    },

    countSelected() {
        const rangeIds = new Set();

        this.selectionIdRanges.forEach((range) => {
            for (let id = range.start_id; id <= range.end_id; id++) {
                rangeIds.add(id);
            }
        });

        const manualSelected = this.selectedTiles.filter((id) => rangeIds.has(id)).length;

        const total = this.allSelected
            ? this.totalTiles - manualSelected
            : rangeIds.size - manualSelected;

        return total;
    },

    updateSelectedTiles() {
        const renderedSelectedIds = Object.values(this.tilesState)
            .filter((tile) => tile?.selected && tile?.id)
            .map((tile) => tile.id);

        this.selectedTiles = [...new Set([...this.selectedTiles, ...renderedSelectedIds])];
    },

    isSelected(tileIndex) {
        return this.tilesState[tileIndex]?.selected;
    },

    isDeleted(tileIndex) {
        return this.tilesState[tileIndex]?.deleted;
    },

    async updateAfterAction(action, turnTo = true) {
        if (!this.totalTilesCount && !this.allSelected) {
            return;
        }

        const unselectedTiles = new Set([
            ...this.unselectedTiles,
            ...this.unselectedAfterShiftSelect,
        ]);

        turnTo
            ? await this.$wire.deleteImages(
                  this.selectedTiles,
                  this.allSelected,
                  this.selectionIdRanges,
                  [...unselectedTiles],
              )
            : await this.$wire.restoreImages(
                  this.selectedTiles,
                  this.allSelected,
                  this.selectionIdRanges,
                  [...unselectedTiles],
              );

        this.allSelected = false;

        Object.values(this.tilesState)
            .filter((tile) => tile?.selected)
            .forEach((tile) => {
                tile[action] = turnTo;
                tile.selected = false;
            });

        this.selectedTiles = [];
        this.unselectedTiles = [];
        this.unselectedAfterShiftSelect = [];
        this.lastSelectedTileIndex = null;
        this.rangesCount = 0;
        this.selectionIdRanges = [];
        this.totalTilesCount = 0;
    },

    selectAll() {
        Object.values(this.tilesState).forEach((tile) => {
            if (tile?.id && !tile?.selected) {
                tile.selected = true;
            }
        });

        this.allSelected = true;
        this.selectedTiles = [];
        this.unselectedTiles = [];
        this.lastSelectedTileIndex = null;
        this.rangesCount = 0;
        this.selectionIdRanges = [];
        this.shiftWasPressed = false;
        this.unselectedAfterShiftSelect = [];
        this.totalTilesCount = this.totalTiles;
    },

    deselectAll() {
        Object.values(this.tilesState).forEach((tile) => {
            if (tile?.id && tile.selected) {
                tile.selected = false;
            }
        });

        this.allSelected = false;
        this.selectedTiles = [];
        this.unselectedTiles = [];
        this.lastSelectedTileIndex = null;
        this.rangesCount = 0;
        this.selectionIdRanges = [];
        this.shiftWasPressed = false;
        this.unselectedAfterShiftSelect = [];
        this.totalTilesCount = 0;
    },

    listenerAddPhotoEvent() {
        this.reset();
        this.start();
    },

    getTileElement(image, index) {
        const tileDiv = this.tileTemplate.cloneNode(true);

        tileDiv.style.width = `${this.tileSize.width}px`;
        tileDiv.style.height = `${this.tileSize.height}px`;
        tileDiv.setAttribute(
            'x-bind:class',
            `{
            'bg-gray-300 opacity-50': isDeleted('${index}'),
            'bg-gray-700': !isDeleted('${index}'),
            'border-green-500': isSelected('${index}'),
            'border-transparent': !isSelected('${index}'),
        }`,
        );

        tileDiv.setAttribute('data-tile-id', index);

        const img = tileDiv.querySelector('img');
        img.src = image.thumb;
        img.setAttribute('data-image-id', image.id);

        if (this.options.preview) {
            img.setAttribute('x-tooltip.placement.left-start', this.fillPreviewMode(image));
        }

        tileDiv.insertAdjacentHTML('beforeend', this.fillActionsAndTags(image, index));

        return tileDiv;
    },

    fillPreviewMode(image) {
        if (!this.options.preview) {
            return '';
        }

        const previewTooltip = `
            <div
                x-data="{ imageLoaded: false }"
                style="width: ${this.config.previewMaxWidth}px; min-height: 200px; max-height: calc(100vh - 20px);"
                class="relative bg-customer-area"
            >
                <img
                    :srcset="imageLoaded ? \`${image.preview} 1x\` : \`${image.thumb} 1x\`"
                    style="max-height: calc(100vh - 20px);"
                    class="w-full object-contain object-center duration-300 opacity-100"
                    fetchpriority="high"
                    loading="eager"
                    @load="imageLoaded = true"
                    alt="Imagem"
                >

                <span class="absolute bg-black bg-opacity-50 text-white p-1 text-xs ellipsis w-full bottom-0">
                    ${image.full_name}
                </span>
            </div>
        `;

        return previewTooltip;
    },

    fillActionsAndTags(image, index) {
        return `${this.topImageItems(image, index)}${this.bottomImageItems(image)}`;
    },

    topImageItems(image, index) {
        const selectCheckbox = this.options.selectable
            ? `
            <div class="absolute top-2 right-2 h-6 w-6 text-center bg-white border-none rounded-full shadow-soft block" x-data>
                <input
                    type="checkbox"
                    class="h-4 w-4 rounded-full p-1 border-2 border-gray-300 text-green-600 form-checkbox focus:ring-green-500"
                    :checked="isSelected('${index}')"
                />
            </div>
        `
            : '';

        const highResLink = this.options.showHighResLink
            ? `
            <a
                href="${image.high_res}"
                target="_blank"
                rel="noopener noreferrer"
                x-on:click="event.stopPropagation();"
                class="cursor-pointer rounded-full p-1 button hover:opacity-75 bg-gray-100"
                id="high-${index}">
                <svg class="w-5 h-5 text-blue-500" x-ref="previewIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
                </svg>
            </a>
        `
            : '';

        return `
            <div class="top-buttons buttons absolute top-0 left-0 w-full h-10 flex items-center justify-between px-2">
              ${selectCheckbox}
              ${highResLink}
            </div>
        `;
    },

    bottomImageItems(image) {
        const imageNameBadge = `
            <span class="text-2xs h-6 gap-1 flex items-center text-gray-900 font-semibold py-0.5 bg-white px-3 rounded-xl -mb-1 -ml-1">
              ${image.name} 
            </span>
        `;

        return `
            <div class="buttons absolute bottom-0 w-full h-10 flex items-center px-2 right-0 justify-end">
              ${this.teamTagsGroup(image)}
              ${imageNameBadge}
            </div>
        `;
    },

    teamTagsGroup({ teams }) {
        if (!this.options.tags) {
            return '';
        }

        const teamTags = teams.map(
            (team) => `
            <div class="p-1 px-2 rounded-md border border-white bg-gray-800 font-semibold flex gap-1">
                <div class="flex-1 overflow-hidden whitespace-nowrap text-ellipsis">
                    ${team.name}
                </div>
            </div>
        `,
        );

        return `
            <div class="flex flex-col gap-1 text-white text-2xs -ml-1 mr-2 overflow-hidden absolute left-2 bottom-2" style="max-width: 60%;">
                ${teamTags.join('')}
            </div>
        `;
    },

    adjustRange(tilesIds) {
        tilesIds.sort((a, b) => a - b);
        const intervals = [];

        let start = tilesIds[0];
        let end = tilesIds[0];

        for (let i = 1; i < tilesIds.length; i++) {
            if (tilesIds[i] === end + 1) {
                end = tilesIds[i];
            } else {
                intervals.push([start, end]);
                start = tilesIds[i];
                end = tilesIds[i];
            }
        }

        intervals.push(start !== end ? [start, end] : start);

        return intervals;
    },
});
